/**
 * Simulation — the deterministic core that binds the World, systems, RNG, map
 * and event bus together and advances them one fixed tick at a time.
 *
 * Everything here is pure with respect to wall-clock time: `step()` takes no
 * arguments and consumes only reproducible inputs, so replaying the same seed +
 * same ordered inputs reproduces the exact same state (foundation for lockstep
 * multiplayer, replays and save games in later sprints).
 */
import { Prng, EventBus, asTick, asSeed } from '@settlers/shared';
import type { Tick } from '@settlers/shared';
import { World } from '../ecs/world.js';
import { Scheduler } from './system.js';
import type { System, SimContext } from './system.js';
import type { GameMap } from '../map/gamemap.js';
import { RoadNetwork } from '../roads/roadNetwork.js';
import { NavGrid } from '../pathfinding/navGrid.js';
import { StateHasher } from '../util/hash.js';

export interface SimulationOptions {
  readonly map: GameMap;
  readonly seed: number;
}

export class Simulation {
  readonly world = new World();
  readonly events = new EventBus();
  readonly prng: Prng;
  readonly map: GameMap;
  readonly roads: RoadNetwork;
  readonly nav: NavGrid;
  private readonly scheduler = new Scheduler();
  private currentTick = 0;

  constructor(options: SimulationOptions) {
    this.map = options.map;
    this.prng = new Prng(asSeed(options.seed));
    this.roads = new RoadNetwork(options.map);
    this.nav = new NavGrid(options.map, this.roads);
  }

  addSystem(system: System): this {
    this.scheduler.add(system);
    return this;
  }

  get tick(): Tick {
    return asTick(this.currentTick);
  }

  get systemNames(): readonly string[] {
    return this.scheduler.names;
  }

  /** Advance exactly one simulation tick. */
  step(): void {
    const ctx: SimContext = {
      world: this.world,
      map: this.map,
      nav: this.nav,
      roads: this.roads,
      prng: this.prng,
      events: this.events,
      tick: this.tick,
    };
    this.scheduler.run(ctx);
    this.currentTick++;
    this.events.emit('sim:tick', { tick: this.tick });
  }

  /** Deterministic fingerprint of the core sim state — same seed + same inputs
   * ⇒ identical hash on every machine. Extend as gameplay state grows. */
  hash(): number {
    const hasher = new StateHasher().int(this.currentTick).int(this.world.entityCount());
    for (const s of this.prng.getState()) hasher.int(s);
    return hasher.value;
  }
}
